import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import prompts, { categories } from '../data/prompts';
import { ArrowLeft, CopyIcon } from '../components/Icons';
import CodeBlock from '../components/CodeBlock';

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <button className="prompt-copy-btn" onClick={handleCopy}>
      <CopyIcon />
      {copied ? 'Copied!' : 'Copy prompt'}
    </button>
  );
}

function DifficultyBadge({ level }) {
  const colors = {
    beginner: '#34d399',
    intermediate: '#fbbf24',
    advanced: '#f472b6',
  };
  return (
    <span
      className="prompt-difficulty"
      style={{
        color: colors[level],
        borderColor: colors[level] + '40',
        background: colors[level] + '10',
      }}
    >
      {level}
    </span>
  );
}

export default function PromptDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const prompt = prompts.find((p) => p.id === id);

  if (!prompt) {
    return (
      <div className="page-enter">
        <p>Prompt not found.</p>
        <button className="back-link" onClick={() => navigate('/prompts')}>
          <ArrowLeft /> Back to library
        </button>
      </div>
    );
  }

  const cat = categories.find((c) => c.id === prompt.category);

  return (
    <div className="page-enter">
      <div className="detail-header">
        <button className="back-link" onClick={() => navigate('/prompts')}>
          <ArrowLeft /> Back to prompt library
        </button>
        <div className="prompt-detail-meta">
          <span
            className="prompt-category-tag"
            style={{
              color: cat?.color,
              borderColor: cat?.color + '40',
              background: cat?.color + '10',
            }}
          >
            {cat?.label}
          </span>
          <DifficultyBadge level={prompt.difficulty} />
        </div>
        <h1 style={{ color: cat?.color }}>{prompt.title}</h1>
        <div className="subtitle">{prompt.description}</div>
      </div>

      {/* When to use */}
      <Section color={cat?.color} title="When to Use">
        <p>{prompt.whenToUse}</p>
      </Section>

      {/* The Prompt */}
      <Section color={cat?.color} title="Prompt Template">
        <div className="prompt-template-wrap">
          <CopyButton text={prompt.prompt} />
          <CodeBlock code={prompt.prompt} title={`${prompt.id}-prompt`} />
        </div>
      </Section>

      {/* Variables */}
      <Section color={cat?.color} title="Template Variables">
        <div className="prompt-variables">
          {prompt.variables.map((v) => (
            <div key={v.name} className="prompt-variable-item">
              <code className="prompt-variable-name">{'{{' + v.name + '}}'}</code>
              <span className="prompt-variable-desc">{v.desc}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* Example Output */}
      <Section color={cat?.color} title="Example Output">
        <CodeBlock code={prompt.exampleOutput} title="example-output" />
      </Section>

      {/* Tips */}
      <Section color={cat?.color} title="Tips & Best Practices">
        <ul className="prompt-tips-list">
          {prompt.tips.map((tip, i) => (
            <li key={i}>{tip}</li>
          ))}
        </ul>
      </Section>

      {/* Tags */}
      <div className="prompt-detail-tags">
        {prompt.tags.map((tag) => (
          <span key={tag} className="prompt-tag">
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}

function Section({ color, title, children }) {
  return (
    <div className="section-block">
      <h2>
        <span className="section-bar" style={{ background: color }} />
        {title}
      </h2>
      {children}
    </div>
  );
}
